import {
    Alert,
    Box,
    Card,
    CardContent,
    Grid,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import { DataUsage } from '@mui/icons-material'

import {
    useGetSubscriptionQuery,
    useGetUsageQuery as useGetBillingUsageQuery,
} from '@/apis/billingApi'
import { useGetUsageQuery } from '@/apis/v2Api'
import { capUsage } from '@/apis/v2Mappers'
import { useProjectId, useProjectToday } from '@/hooks/useReportArgs'
import { monthStart } from '@/utils/dates'

import { Kpi, ReportHeader, ReportStatus, SectionCard, fmtNum, fmtPct } from './common'

/**
 * Events collected this month (v2 `/reports/usage`, per sub-tenant, internal
 * traffic included) next to the organization's plan cap (billing API).
 */
export function UsagePage() {
    const projectId = useProjectId()
    const to = useProjectToday()
    const from = monthStart(to)
    const usage = useGetUsageQuery({ projectId, from, to, subTenant: null })
    const { data: sub } = useGetSubscriptionQuery()
    const { data: billing } = useGetBillingUsageQuery()

    const cap =
        sub?.eventsLimit ?? (billing?.plan === 'free' ? billing.freeEventsIncluded : undefined)
    const metered = billing?.eventsUsed ?? sub?.eventsUsed
    const share = capUsage(metered, cap)

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Usage"
                subtitle={`This month (${from} → ${to}). Plan caps apply to the whole organization.`}
            />
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Kpi
                        title="Events this month (this project)"
                        value={fmtNum(usage.data?.total)}
                        hint="Includes internal traffic"
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Kpi
                        title="Organization events (billing meter)"
                        value={fmtNum(metered)}
                        hint="Updated by the billing job; may lag behind collection"
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Kpi
                        title="Plan cap"
                        value={cap ? fmtNum(cap) : 'Pay as you go'}
                        hint={billing ? `Plan: ${billing.plan}` : undefined}
                    />
                </Grid>
            </Grid>

            {share !== null && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2">Plan cap used</Typography>
                            <Typography variant="subtitle2">{fmtPct(share)}</Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min(100, share * 100)}
                            color={share >= 1 ? 'error' : share >= 0.8 ? 'warning' : 'primary'}
                            sx={{ height: 10, borderRadius: 5 }}
                        />
                        {share >= 1 && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                The organization has reached its plan cap. See Billing to raise it.
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            )}

            <SectionCard title="Events by sub-tenant">
                <ReportStatus
                    isLoading={usage.isLoading}
                    error={usage.error}
                    isEmpty={(usage.data?.rows ?? []).length === 0}
                    emptyIcon={<DataUsage />}
                    emptyTitle="No events this month"
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Sub-tenant</TableCell>
                                <TableCell align="right">Events</TableCell>
                                <TableCell align="right">Sessions</TableCell>
                                <TableCell sx={{ width: 200 }}>Share</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(usage.data?.rows ?? []).map(r => (
                                <TableRow key={r.key} hover>
                                    <TableCell
                                        sx={{
                                            fontFamily: r.key ? 'monospace' : undefined,
                                            fontStyle: r.key ? undefined : 'italic',
                                        }}
                                    >
                                        {r.label}
                                    </TableCell>
                                    <TableCell align="right">{fmtNum(r.events)}</TableCell>
                                    <TableCell align="right">{fmtNum(r.sessions)}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={r.share * 100}
                                                sx={{ flex: 1, height: 6, borderRadius: 3 }}
                                            />
                                            <Typography variant="caption">
                                                {fmtPct(r.share)}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </ReportStatus>
            </SectionCard>
        </Box>
    )
}
